import { useSnackbar } from "notistack";
import { isNil } from "ramda";
import { useCallback, useState } from "react";
import getAddressByZipCode from "../../services/check-form/getAddressByZipCode";
import isNilOrEmpty from "../../utils/isNilOrEmpty";
import {
  StyledInput,
  StyledInputMask,
  StyledInputWrapper,
  StyledLabel,
} from "../form-input/form-input-styles";
import { TFormInputProps } from "../form-input/types";
import {
  StyledContainer,
  StyledEnableAddressButton,
} from "./address-input-styles";

const AddressInput = ({
  label,
  name,
  placeHolder,
  setValue,
  value,
}: TFormInputProps) => {
  const [isDisabled, setIsDisabled] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleGetAddress = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue((prev) => ({ ...prev, [name]: e.target.value }));
      const cepSize = e.target.value.replaceAll("_", "");

      if (cepSize.length >= 9) {
        const addressResponse = await getAddressByZipCode({
          zipcode: e.target.value,
          feedbackMessage: enqueueSnackbar,
        });

        if (addressResponse?.erro === "true") return;

        if (isNilOrEmpty(addressResponse)) {
          setIsDisabled(false);
          return enqueueSnackbar("CEP não é válido", { variant: "warning" });
        }

        const address = String(addressResponse?.logradouro);
        const neighborhood = String(addressResponse?.bairro);
        const state = String(addressResponse?.uf);
        const city = String(addressResponse?.localidade);
        setValue((prev) => ({ ...prev, address, neighborhood, state, city }));
      }
    },
    [enqueueSnackbar, setValue, name]
  );

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <StyledLabel textColor="#FFFFFF">{label}</StyledLabel>
      <StyledContainer>
        <StyledInputWrapper>
          <StyledInputMask
            value={value.zipCode}
            mask="99999-999"
            name={name}
            onChange={handleGetAddress}
            required
            placeholder={placeHolder}
            type="tel"
          />
        </StyledInputWrapper>
        <StyledEnableAddressButton
          onClick={() =>
            openInNewTab(
              "https://buscacepinter.correios.com.br/app/endereco/index.php"
            )
          }
          type="button"
        >
          Não sei meu CEP
        </StyledEnableAddressButton>
      </StyledContainer>
      <StyledContainer>
        <StyledInput
          value={value.address}
          variant="standard"
          name="address"
          onChange={(e) =>
            setValue((prev) => ({ ...prev, address: e.target.value }))
          }
          required
          placeholder="Endereço"
          disabled={isDisabled}
        />

        <StyledInput
          value={value.number}
          variant="standard"
          name="number"
          onChange={(e) => {
            const isJustNumbers = e.target.value.match(/\D/);
            const isMaxLength = e.target.value.length > 9;

            if (isNil(isJustNumbers) && !isMaxLength) {
              setValue((prev) => ({ ...prev, number: e.target.value }));
            }
          }}
          required
          placeholder="N°"
          type="tel"
          issmall={1}
        />

        <StyledInput
          value={value.complement}
          variant="standard"
          name="complement"
          onChange={(e) => {
            setValue((prev) => ({ ...prev, complement: e.target.value }));
          }}
          required
          placeholder="Complemento"
        />
      </StyledContainer>

      <StyledContainer>
        <StyledInput
          value={value.neighborhood}
          variant="standard"
          name="neighborhood"
          onChange={(e) => {
            setValue((prev) => ({ ...prev, neighborhood: e.target.value }));
          }}
          required
          placeholder="Bairro"
        />

        <StyledInput
          value={value.city}
          variant="standard"
          name="city"
          onChange={(e) => {
            setValue((prev) => ({ ...prev, city: e.target.value }));
          }}
          required
          placeholder="Cidade"
        />

        <StyledInput
          value={value.state}
          variant="standard"
          name="state"
          onChange={(e) => {
            setValue((prev) => ({ ...prev, state: e.target.value }));
          }}
          required
          placeholder="Estado"
          issmall={1}
        />
      </StyledContainer>
    </>
  );
};

export default AddressInput;
